/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { AnimationClassNames, getFocusStyle, getTheme, mergeStyles } from '@fluentui/react/lib/Styling';
import { Text } from '@fluentui/react/lib/Text';
import { getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist, important, joinClassNames } from 'src/core/themes/luisStylist';

export type FilePickerProps = {
    /**
     * Whether the file input is required or not.
     */
    required?: boolean;

    /**
     * Whether the file picker should autofocus on mount or not.
     */
    autoFocus?: boolean;

    /**
     * The label to display.
     */
    label?: string;

    /**
     * A callback that gets fired when the file(s) are selected
     */
    onFilesPicked?: (files: FileList) => void;

    /**
     * An error message to display if defined.
     */
    errorMessage?: string;

    /**
     * Allows the user to select more than one file.
     */
    multiple?: boolean;

    /**
     * Defines the file types the file input should accept.
     */
    accept?: string;

    /**
     * Whether the input is disabled or not.
     */
    disabled?: boolean;
};

const messages = defineMessages({
    defaultPlaceholder: {
        id: 'core.filePicker.defaultLabel',
        defaultMessage: 'File (JSON file)'
    },
    required: {
        id: 'core.filePicker.required',
        defaultMessage: 'Required'
    }
});

const { styleInput } = getStylist('FilePicker');
const InputFile = styleInput('InputFile', important({ width: 'fit-content', margin: '0px' }));

const coloringClassName = getStylist('FilePicker').getClassName('ErrorMessage', theme => ({ color: theme.errorMessageColor }));
const errorMessageClassName = joinClassNames(coloringClassName, AnimationClassNames.slideDownIn20);
const focusStyles = mergeStyles(getFocusStyle(getTheme()));

/**
 * Represents a file picker to use in forms.
 */
export const FilePicker = (props: FilePickerProps) => {
    const { localize } = useLocalization();
    const {
        accept,
        errorMessage,
        onFilesPicked,
        required = true,
        multiple = false,
        disabled = false,
        autoFocus = false,
        label = localize(messages.defaultPlaceholder)
    } = props;

    const inputId = React.useRef<string>(getId());

    const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { files } = e.target as HTMLInputElement;

        if (onFilesPicked && files) {
            onFilesPicked(files);
        }
    };

    return (
        <Stack tokens={{ childrenGap: 5 }}>
            <Stack tokens={{ childrenGap: 5 }}>
                <Label
                    required={required}
                    htmlFor={inputId.current}
                    aria-label={`${label} ${errorMessage ?? ''} ${required ? localize(messages.required) : ''}`}>
                    {label}
                </Label>

                <InputFile
                    type="file"
                    accept={accept}
                    required={required}
                    multiple={multiple}
                    onChange={onChange}
                    disabled={disabled}
                    id={inputId.current}
                    autoFocus={autoFocus}
                    className={focusStyles}
                    aria-required={required}
                />
            </Stack>

            <Text variant="small" className={errorMessageClassName}>
                {errorMessage}
            </Text>
        </Stack>
    );
};
